import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';

/**
 * Módulo colapsable de los working views (rediseño Tech Premium, mockup B-working-views-full).
 * Caja base: header (ícono + título + chip + caret) colapsable, con PREVIEW de una línea cuando
 * está cerrado (principio del rediseño: reorganizar ≠ quitar — lo colapsado siempre dice qué hay).
 * El estado abierto/cerrado persiste en localStorage por módulo(+claim) — mismo patrón que
 * usePersistentState pero durable entre sesiones (localStorage, no sessionStorage).
 */
function useOpenState(key: string, initial: boolean): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw != null ? raw === '1' : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, open ? '1' : '0');
    } catch {
      /* modo privado / cuota: el estado vive solo en memoria */
    }
  }, [key, open]);
  return [open, setOpen];
}

export function Module({
  id,
  icon: Icon,
  title,
  chip,
  preview,
  defaultOpen = true,
  children,
}: {
  /** Único por módulo+claim, ej. `mod:info-pending:${claimId}` — es la key de persistencia. */
  id: string;
  icon?: LucideIcon;
  title: ReactNode;
  /** Chip de conteo/estado a la derecha del título (ej. <RChip variant="warn">3</RChip>). */
  chip?: ReactNode;
  /** Línea de resumen visible SOLO cuando el módulo está cerrado. */
  preview?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useOpenState(`mod:${id}`, defaultOpen);
  return (
    <section className={`module${open ? '' : ' closed'}`}>
      <div
        className="module-head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(!open);
          }
        }}
      >
        {Icon && (
          <span className="ico">
            <Icon size={14} strokeWidth={1.75} />
          </span>
        )}
        <span className="module-title">{title}</span>
        {chip}
        <span className="caret">
          <ChevronDown size={14} strokeWidth={2} />
        </span>
      </div>
      {preview != null && <div className="preview">{preview}</div>}
      {open && <div className="module-body">{children}</div>}
    </section>
  );
}
